import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import BSL_Button from "../components/BSL_Button";
import BSL_TextInput from "../components/BSL_TextInput";
import { fetchTimeout, defaultColour, RehydrateThumbnails } from "../components/Common";
import { updateAPP } from "../redux_store/appState";
import { Palette, API_BASE } from "../AppConfig";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import ErrorIcon from "@material-ui/icons/Error";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import { Popper, Tooltip, LinearProgress, Chip, TablePagination } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import SegmentedButtonGroup from "../components/SegmentedButtonGroup";
import * as BSL_Common from "../components/BSL_CommonControls";
import "react-json-view-lite/dist/index.css";
import BSL_Select, { BSL_MultiSelect } from "../components/BSL_Select";
import ManageProductListControlCodes from "./ManageProductListControlCodes";
import ManageProductListAncillary from "./ManageProductListAncillary";
import Autocomplete from "react-autocomplete";
import ColourChip from "../components/ColourChip";
import ManageProductListItem from "./ManageProductListItem";
import BSL_ControlContainer from "../components/BSL_ControlContainer";
import Draggable from "react-draggable";
import ManageProductListItemHistory from "./ManageProductListItemHistory";
import WarningIcon from "@material-ui/icons/Warning";
import Select from "react-select";
import ManageDynamicCodes from "./ManageDynamicCodes";
import { buildWZColumns, extractLengths, findBestMatch, getKeys, hasMissingEntries } from "./Helpers/DynamicsHelper";

const useStyles = makeStyles((theme) => ({
    root: {
        height: "92%",
        flex: "1",
        width: "100%",
        maxHeight: "100%",
        margin: "0px 10px",
        color: theme.brand.FIELDERS ? "inherit" : Palette.Lysaght.MidGrey,
    },
    container: {
        height: "90vh",
        maxHeight: "calc(90vh - 64px)",
    },
    row: {
        "tr:hover": {
            backgroundColor: "rgb(7, 50, 50, 0.42)",
        },
    },
    rowCell: {
        fontSize: "12px",
        border: "none",
        padding: "2px 0px 2px 24px",
        color: theme.brand.FIELDERS ? "inherit" : Palette.Lysaght.MidGrey,
        "&:hover": {
            backgroundColor: "rgb(7, 177, 77, 0.42)",
            cursor: "pointer",
        },
    },
    headingCell: {
        fontSize: "11px",
        border: "none",
        padding: "2px 0px 2px 24px",
        textTransform: "uppercase",
        color: "#ffffff",
        fontWeight: theme.brand.FIELDERS ? "inherit" : "600",
        fontFamily: theme.typography.fontFamilySecondary,
        backgroundColor: Palette.FieldersBlue,
        lineHeight: "normal",
    },
    thumbnail: {
        // ⚠️ object-fit is not supported by IE 11.
        objectFit: "contain",
        width: "32px",
        height: "32px",
        "&:hover": {
            backgroundColor: "#d5d5d5",
        },
    },
    thumbnailTooltip: {
        // ⚠️ object-fit is not supported by IE 11.
        objectFit: "contain",
        maxWidth: "400px",
        maxHeight: "400px",
        flex: 1,
        ".tooltip:before": {
            left: "20px",
        },
    },
    editButton: {
        cursor: "pointer",
        color: Palette.FieldersBlue,
        "&:hover": {
            backgroundColor: "#d5d5d5",
            cursor: "pointer",
        },
    },
    moreButton: {
        padding: "0px",
        borderRadius: "0%",
    },
    no_job_message: {
        color: "grey",
        height: "100px",
        fontSize: "20px",
        fontStyle: "italic",
        paddingTop: "40px",
        position: "absolute",
        left: "47%",
        top: "208px",
    },
    search_container: {
        borderRadius: theme.brand.LYSAGHT ? "0px" : "50px",
        background: "white",
        border: "1px solid #6B8799",
        width: "200px",
        maxHeight: "32px",
        borderRadius: "0px",
        marginRight: "16px",
        display: "flex",
    },
    search_icon: {
        color: theme.brand.LYSAGHT ? Palette.Lysaght.DarkBlue : "grey",
        width: "0.75em",
        height: "0.75em",
        marginLeft: "7px",
        verticalAlign: "-webkit-baseline-middle",
        marginTop: theme.brand.LYSAGHT ? "5px" : "inherit",
    },
    drag_bar: {
        width: "30px",
        height: "1px",
        marginBottom: "1px",
        background: "#00000030",
    },
}));

const D365Editor = ({ row, value, setValue, saveValue, value2 }) => {
    // Maintain local options for the select dropdown.
    const [localOptions, setLocalOptions] = useState(
        Array.from(new Set((value || []).map((code) => code.dynamicsCode))).map((code) => ({
            value: code,
            label: code,
        }))
    );

    // Handler to update a row's field.
    const handleChange = (index, field, newValue) => {
        const updatedValues = [...value];
        updatedValues[index][field] = newValue;
        setValue(updatedValues);
    };

    // Handler to add a new row.
    const handleAddRow = () => {
        setValue([...value, { dynamicsCode: "", lyvesCode: "" }]);
    };

    // Handler to remove a row.
    const handleRemoveRow = (index) => {
        setValue(value.filter((_, i) => i !== index));
    };

    return (
        <>
            <div style={{ fontSize: "14px" }}>
                <label htmlFor="d365Dropdown">D365 Linked Codes</label>
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
                    <thead>
                        <tr>
                            <th>Dynamics Code</th>
                            <th>Lyves Code</th>
                            <th>Remove Option</th>
                        </tr>
                    </thead>
                    <tbody>
                        {value.map((item, index) => (
                            <tr key={index}>
                                <td style={{ padding: "5px" }}>
                                    <input
                                        type="text"
                                        value={item.dynamicsCode}
                                        onChange={(e) => handleChange(index, "dynamicsCode", e.target.value)}
                                        style={{ fontSize: "14px", padding: "5px", width: "100%" }}
                                    />
                                </td>
                                <td style={{ padding: "5px" }}>
                                    <input
                                        type="text"
                                        value={item.lyvesCode}
                                        onChange={(e) => handleChange(index, "lyvesCode", e.target.value)}
                                        style={{ fontSize: "14px", padding: "5px", width: "100%" }}
                                    />
                                </td>
                                <td style={{ padding: "5px", textAlign: "center" }}>
                                    <button
                                        style={{
                                            color: "red",
                                            cursor: "pointer",
                                            background: "none",
                                            border: "none",
                                        }}
                                        onClick={() => handleRemoveRow(index)}
                                    >
                                        ❌
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <button
                    style={{
                        marginTop: "10px",
                        padding: "5px 10px",
                        background: "#007bff",
                        color: "#fff",
                        border: "none",
                        cursor: "pointer",
                    }}
                    onClick={handleAddRow}
                >
                    ➕ Add Row
                </button>
            </div>
            {value2 !== "NO BUTTON" && (
                <BSL_Button onClick={() => saveValue(row, "MatchedItems", value)}>UPDATE</BSL_Button>
            )}
        </>
    );
};

const columns = [
    {
        id: "GroupingName",
        label: "Category",
        editor: (row, value, setValue, saveValue, value2) => {
            if (value === undefined) setValue(row.GroupingName);
            let original_value = row.GroupingName;
            return (
                <>
                    <BSL_Select
                        label={"Category"}
                        style={{ width: "200px", color: "white" }}
                        value={value}
                        tight={true}
                        items={[
                            { Key: "", Display: "ALL" },
                            { Key: "Steel Roofing", Display: "Steel Roofing" },
                            { Key: "PolyCarbonate Roofing", Display: "PolyCarbonate Roofing" },
                            { Key: "Fibreglass Roofing", Display: "Fibreglass Roofing" },
                            { Key: "Back Channel", Display: "Back Channel" },
                            { Key: "Downpipes", Display: "Downpipes & Accessories" },
                            { Key: "Gutters", Display: "Gutters & Accessories" },
                            { Key: "Flashing", Display: "Flashing" },
                            { Key: "Beams", Display: "Beams" },
                            { Key: "Posts", Display: "Posts" },
                            { Key: "Brackets", Display: "Brackets" },
                            { Key: "Connectors", Display: "Connectors" },
                            { Key: "Hardware", Display: "Hardware" },
                            { Key: "Slats", Display: "Slats" },
                            { Key: "Misc", Display: "Misc" },
                        ]}
                        onChange={(e) => {
                            setValue(e.target.value);
                            // setBrandFilter(e.target.value);
                        }}
                        helperText={original_value ? "currently: " + original_value : ""}
                    />
                    {value2 !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "GroupingName", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "Image_b64",
        label: "Image",
        format: (value) => {
            if (value) return <img style={{ maxHeight: "2rem", maxWidth: "4rem" }} src={value} />;
            return "";
        },
        editor: (row, value, setValue, saveValue, value2, setValue2, param) => {
            if (value === undefined) setValue(row.Image_b64);
            if (!value2) setValue2({});
            let original_value = row.Image_b64;

            const dragOver = (e) => {
                let newvalue2 = { ...value2 };
                newvalue2.background = "#111111AA";
                setValue2(newvalue2);
                e.preventDefault();
            };
            const dragLeave = (e) => {
                let newvalue2 = { ...value2 };
                newvalue2.background = "rgb(134 134 134 / 36%)";
                setValue2(newvalue2);
                e.preventDefault();
            };
            const fileDrop = (e) => {
                let newvalue2 = { ...value2 };
                newvalue2.background = "rgb(134 134 134 / 36%)";
                setValue2(newvalue2);
                e.preventDefault();
                const files = e.dataTransfer.files;

                if (files.length) {
                    handleFiles(files);
                }
            };

            const uploadFile = (e) => {
                if (e?.target?.files) handleFiles(e.target.files);
            };

            const handleFiles = (files) => {
                if (files.length === 0) return;
                let file = files[0];
                if (
                    file.name.toLowerCase().endsWith(".png") ||
                    file.name.toLowerCase().endsWith(".jpg") ||
                    file.name.toLowerCase().endsWith(".gif")
                ) {
                    if (file.size > 10000) {
                        alert("File is too big! Less thank 10kB please");
                        return;
                    }

                    var reader = new FileReader();
                    reader.onload = function (e) {
                        setValue(e.target.result);
                    };
                    reader.readAsDataURL(file);
                }
            };

            return (
                <>
                    <BSL_ControlContainer
                        label={"Image"}
                        style={{ color: "white" }}
                        tight={true}
                        helperText={"max 10kb"}
                    >
                        <IconButton
                            aria-label="delete"
                            size="small"
                            onClick={() => setValue("")}
                            style={{ color: "black", position: "absolute", right: "5px", marginTop: "12px" }}
                        >
                            <DeleteIcon />
                        </IconButton>
                        <input
                            accept="image/gif, image/jpeg, image/png"
                            // className={classes.input}
                            style={{ display: "none" }}
                            id="raised-button-file"
                            type="file"
                            onChange={uploadFile}
                        />
                        <label htmlFor="raised-button-file">
                            <div
                                onDragOver={dragOver}
                                // onDragEnter={dragEnter}
                                onDragLeave={dragLeave}
                                onDrop={fileDrop}
                                style={{
                                    background: value2?.background ? value2?.background : "rgb(134 134 134 / 36%)",
                                    cursor: "pointer",
                                    textAlign: "center",
                                }}
                            >
                                <div
                                    className="checkerBG"
                                    style={{
                                        backgroundColor: "white",
                                        minHeight: "50px",
                                    }}
                                >
                                    <img
                                        style={{
                                            maxHeight: "50px",
                                            maxWidth: "150px",
                                            textAlign: "center",
                                        }}
                                        src={value}
                                    />
                                </div>

                                <div
                                    style={{
                                        textAlign: "center",
                                        color: "white",
                                        fontSize: "12px",
                                        height: "50px",
                                    }}
                                >
                                    {"Click or Drag & Drop image file to upload"}
                                </div>
                            </div>
                        </label>
                    </BSL_ControlContainer>

                    {param !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "Image_b64", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "ProductIndex",
        label: "Index",
        editor: (row, value, setValue, saveValue, value2) => {
            if (value === undefined) setValue(row.ProductIndex);
            let original_value = row.ProductIndex;
            return (
                <>
                    <BSL_TextInput
                        label={"Index"}
                        style={{ width: "150px", color: "white" }}
                        value={value}
                        autoFocus
                        tight={true}
                        onEnter={() => saveValue(row, "ProductIndex", value)}
                        onChange={(value) => {
                            setValue(value);
                        }}
                        helperText={original_value ? "currently: " + original_value : ""}
                    />

                    {value2 !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "ProductIndex", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "ProductCode",
        label: "LyvesCode",
        editor: (row, value, setValue, saveValue, value2) => {
            if (value === undefined) setValue(row.ProductCode);
            let original_value = row.ProductCode;
            return (
                <>
                    <BSL_TextInput
                        label={"Lyves Code"}
                        style={{ width: "150px", color: "white" }}
                        value={value}
                        autoFocus
                        tight={true}
                        onEnter={() => saveValue(row, "ProductCode", value)}
                        onChange={(value) => {
                            setValue(value);
                        }}
                        helperText={original_value ? "currently: " + original_value : ""}
                    />

                    {value2 !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "ProductCode", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "MatchedItems",
        label: "Dynamics Codes",
        format: (value, row) => {
            // 1) Grab the same “materials” config that ManageDynamicCodes uses:
            let materials = window.config;

            // 2) Recompute promptLengths → wz_columns → keys exactly as the child does:
            const promptLengths = extractLengths(row?.PromptTextCodes);
            const wz_columns = buildWZColumns(promptLengths);
            const keys = getKeys(row, materials);

            // 3) Safely pull out matchedItems from the row (fall back to empty object):
            let matchedItems = row?.MatchedItems || {};

            // 4) Loop through every (key × column) to see if any cell would be “❌”
            let missingCodes = false;
            for (let key of keys) {
                for (let column of wz_columns) {
                    const colId = column.id;

                    // → direct lookup
                    let directValue = undefined;
                    if (matchedItems[key] !== undefined && matchedItems[key][colId] !== undefined) {
                        directValue = matchedItems[key][colId];
                    }

                    // → inherited lookup
                    const matchedArray = Object.values(matchedItems);
                    const bestMatch = findBestMatch(matchedArray, key, colId, row?.ProductCode);
                    const inherit_value = bestMatch ? bestMatch.dynamicsCode || "" : "";

                    // Skip "DEFAULT" if there are multiple keys and the column is "Key"
                    if (colId === "Key") {
                        continue;
                    }

                    // If neither a direct value nor an inherited value exists, mark as missing and break
                    if (directValue === undefined && !inherit_value) {
                        missingCodes = true;
                        break;
                    }
                }
                if (missingCodes) break;
            }

            // 5) Build the PRN_Code_Text exactly as before:
            let PRN_Code_Text = "PRN Codes ✔️";
            if (!value || value.length === 0) {
                PRN_Code_Text = "🔴";
            } else if (value.length === 1) {
                PRN_Code_Text = value[0]?.dynamicsCode;
            } else if (missingCodes) {
                PRN_Code_Text = "Missing Codes ⚠️";
            }

            return (
                <div
                    style={{
                        color: "gray",
                        fontSize: "66%",
                        whiteSpace: "break-spaces",
                    }}
                >
                    {PRN_Code_Text}
                </div>
            );
        },
        editor: (row, value, setValue, saveValue, value2, setValue2, pricefileDescSummary) => {
            let PRN_Code_Text = "";
            let materials = window.config;
            if (!value || value.length === 0) PRN_Code_Text = "";
            if (value && value.length === 1) PRN_Code_Text = value[0]?.dynamicsCode;

            if (!row?.ProductCode) return null;

            // Static Lyves Code
            if (!["#", "@", "*"].some((char) => row.ProductCode.includes(char))) {
                return (
                    <>
                        <BSL_TextInput
                            label={"PRN CODE"}
                            style={{ width: "150px", color: "white" }}
                            value={PRN_Code_Text}
                            autoFocus
                            tight={true}
                            onEnter={() => saveValue(row, "MatchedItems", value)}
                            onChange={(newValue) => {
                                const updatedValues = [...value];
                                updatedValues[0]["dynamicsCode"] = newValue;
                                setValue(updatedValues);
                            }}
                            helperText={
                                row.MatchedItems?.[0]?.dynamicsCode
                                    ? "currently: " + row.MatchedItems[0].dynamicsCode
                                    : ""
                            }
                        />

                        {value2 !== "NO BUTTON" && (
                            <BSL_Button onClick={() => saveValue(row, "MatchedItems", value)}>UPDATE</BSL_Button>
                        )}
                    </>
                );
            }

            return (
                <ManageDynamicCodes
                    row={row}
                    onClose={() => saveValue(null)}
                    saveValue={saveValue}
                    pricefileDescSummary={pricefileDescSummary}
                    materials={materials}
                />
            );
        },
    },

    {
        id: "Brand",
        label: "Brand",
        format: (value) => {
            if (value) return value;
            return "ALL";
        },
        editor: (row, value, setValue, saveValue, value2) => {
            if (value === undefined) setValue(row.Brand);
            let original_value = row.Brand;
            return (
                <>
                    <BSL_Select
                        label={"Brand"}
                        style={{ width: "90px", color: "white" }}
                        value={value}
                        tight={true}
                        items={[
                            { Key: "", Display: "ALL" },
                            { Key: "CDS", Display: "CDS" },
                            { Key: "LLD", Display: "LLD" },
                        ]}
                        onChange={(e) => {
                            setValue(e.target.value);
                            // setBrandFilter(e.target.value);
                        }}
                        helperText={original_value ? "currently: " + original_value : ""}
                    />
                    {value2 !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "Brand", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "ProductGroup",
        label: "Product Group",
        format: (value, row) => {
            return <>{row?.ProductGroup + "-" + row?.SubGroup}</>;
        },
        editor: (row, value, setValue, saveValue, value2, setValue2, param) => {
            if (value === undefined) setValue(row.ProductGroup);
            if (value2 === undefined) setValue2(row.SubGroup);
            return (
                <>
                    <BSL_Common.SectionRow>
                        <BSL_TextInput
                            label={"Product Group"}
                            style={{ width: "50px", color: "white" }}
                            value={value ? value : row.ProductGroup}
                            preValidate={"numbersOnly"}
                            tight={true}
                            autoFocus
                            onEnter={() =>
                                saveValue(
                                    row,
                                    "ProductGroup",
                                    value ? value : row.ProductGroup,
                                    "SubGroup",
                                    value2 ? value2 : row.SubGroup
                                )
                            }
                            onChange={(val) => {
                                setValue(val);
                            }}
                            helperText={row?.ProductGroup ? "currently: " + row?.ProductGroup : ""}
                        />
                        <BSL_TextInput
                            label={"Sub Group"}
                            preValidate={"numbersOnly"}
                            style={{ width: "150px", color: "white" }}
                            value={value2 ? value2 : row.SubGroup}
                            tight={true}
                            onEnter={() =>
                                saveValue(
                                    row,
                                    "ProductGroup",
                                    value ? value : row.ProductGroup,
                                    "SubGroup",
                                    value2 ? value2 : row.SubGroup
                                )
                            }
                            onChange={(val) => {
                                setValue2(val);
                            }}
                            helperText={row?.SubGroup ? row?.SubGroup : ""}
                        />
                    </BSL_Common.SectionRow>

                    {param !== "NO BUTTON" && (
                        <BSL_Button
                            onClick={() =>
                                saveValue(
                                    row,
                                    "ProductGroup",
                                    value ? value : row.ProductGroup,
                                    "SubGroup",
                                    value2 ? value2 : row.SubGroup
                                )
                            }
                        >
                            UPDATE
                        </BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "ProductDescription",
        label: "Description",
        editor: (row, value, setValue, saveValue, value2) => {
            if (value === undefined) setValue(row.ProductDescription);
            let original_value = row.ProductDescription;
            return (
                <>
                    <BSL_TextInput
                        label={"Description"}
                        style={{ width: "500px", color: "white" }}
                        value={value}
                        autoFocus
                        tight={true}
                        onEnter={() => saveValue(row, "ProductDescription", value)}
                        onChange={(value) => {
                            setValue(value);
                        }}
                        helperText={original_value ? "currently: " + original_value : ""}
                    />

                    {value2 !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "ProductDescription", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "Cost",
        label: "Cost",
        hideInTable: true,
        editor: (row, value, setValue, saveValue, value2) => {
            if (value === undefined) setValue(row.Cost);
            let original_value = row.Cost;
            return (
                <>
                    <BSL_TextInput
                        label={"Cost"}
                        style={{ width: "90px", color: "white" }}
                        value={value}
                        autoFocus
                        tight={true}
                        onEnter={() => saveValue(row, "Cost", value)}
                        onChange={(value) => {
                            setValue(value);
                        }}
                        helperText={original_value ? "currently: " + original_value : ""}
                    />

                    {value2 !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "Cost", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "Markup",
        label: "Mark up",
        editor: (row, value, setValue, saveValue, value2) => {
            if (value === undefined) setValue(row.Markup);
            let original_value = row.Markup;
            return (
                <>
                    <BSL_TextInput
                        label={"Markup"}
                        style={{ width: "40px", color: "white" }}
                        value={value}
                        autoFocus
                        tight={true}
                        onEnter={() => saveValue(row, "Markup", value)}
                        onChange={(value) => {
                            setValue(value);
                        }}
                        helperText={original_value ? "currently: " + original_value : ""}
                    />

                    {value2 !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "Markup", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "SellingUOM",
        label: "Unit",
        fieldInfo: "use a number to nominate a pack size, otherwise EA or LM",
        editor: (row, value, setValue, saveValue, value2) => {
            if (value === undefined) setValue(row.SellingUOM);
            let original_value = row.SellingUOM;
            return (
                <>
                    <Autocomplete
                        items={["EA", "LM", "100", "250", "500", "1000"]}
                        shouldItemRender={(item, value) => true}
                        getItemValue={(item) => item}
                        autoHighlight={false}
                        renderItem={(item, highlighted) => (
                            <div key={item.id} style={{ backgroundColor: highlighted ? "#eee" : "transparent" }}>
                                {item}
                            </div>
                        )}
                        wrapperStyle={{
                            zIndex: "999999",
                        }}
                        menuStyle={{
                            zIndex: "999999",
                            position: "absolute",
                            backgroundColor: "whitesmoke",
                            boxShadow: "1px 1px 4px",
                            fontSize: "12px",
                            left: "auto",
                            top: "auto",
                            marginLeft: "9px",
                            marginTop: "-33px",
                            textAlign: "left",
                            maxHeight: "250px",
                            overflowY: "auto",
                        }}
                        inputProps={{
                            style: {
                                zIndex: "999999",
                                font: "200 12px Univers45",
                                width: "95%",
                                padding: "4px 0px 4px 8px !important",
                            },
                        }}
                        renderInput={(props) => {
                            return (
                                // <BSL_TextInput
                                //     label={"Add New Colour"}
                                //     style={{ width: "300px", color: "white" }}
                                //     value={value2}
                                //     autoFocus
                                //     tight={true}
                                //     onEnter={(e) => {
                                //         let new_col = e.target.value.replace("List: ", "").split("(")[0].trim();
                                //         setValue(value + (value == "" ? "" : ",") + new_col);
                                //         setValue2("");
                                //     }}
                                //     onChange={(value) => {
                                //         setValue2(value);
                                //     }}
                                //     InputProps={{
                                //         style: { fontSize: "80%" },
                                //         ...props,
                                //     }}
                                //     helperText={helperText}
                                //     helperTextStyle={{
                                //         whiteSpace: "break-spaces",
                                //         backgroundColor: "#ffe7c3",
                                //         padding: "5px",
                                //         borderRadius: "5px",
                                //     }}
                                // />

                                <BSL_TextInput
                                    label={"Unit"}
                                    style={{ width: "150px", color: "white" }}
                                    value={value}
                                    autoFocus
                                    tight={true}
                                    onEnter={() => saveValue(row, "SellingUOM", value)}
                                    onChange={(value) => {
                                        setValue(value);
                                    }}
                                    InputProps={{
                                        ...props,
                                    }}
                                    helperText={
                                        (original_value ? "currently: " + original_value : "") +
                                        ". Number to nominate pack size, otherwise EA or LM"
                                    }
                                />
                            );
                        }}
                        value={value}
                        onChange={(e) => setValue(value)}
                        onSelect={(value) => {
                            setValue(value);
                            saveValue(row, "SellingUOM", value);
                        }}
                    />

                    {value2 !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "SellingUOM", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "Colours",
        label: "Colours",
        format: (value, row) => {
            if (!value) {
                return (
                    <div style={{ color: "#bebebe", fontSize: "66%", fontStyle: "italic" }}>
                        default:&nbsp;{defaultColour(row?.ProductDescription, row?.GroupingName, row?.Brand)}
                    </div>
                );
            }

            let valid = true;

            let materials = window.config;
            if (!value.includes(",")) {
                valid = false;
                if (materials?.colour_arrays?.[value.trim()]) {
                    valid = true;
                }
                if (materials?.colour_map?.[value.trim()]) {
                    valid = true;
                }
            }

            return (
                <div style={{ color: "black", fontSize: "66%" }}>
                    {!valid && (
                        <WarningIcon
                            style={{
                                width: "17px",
                                height: "13px",
                                color: "#e29d35",
                            }}
                        />
                    )}
                    {value}
                </div>
            );
        },
        editor: (row, value, setValue, saveValue, value2, setValue2, param, materials) => {
            if (value === undefined) setValue(row.Colours);
            let original_value = row.Colours;

            let colour_keys = [];
            for (let list_key in materials.colour_arrays) {
                let list = materials.colour_arrays?.[list_key];
                colour_keys.push("List: " + list_key + " (" + list.length + " Colours)");
            }

            colour_keys = [...colour_keys, ...Object.keys(materials.colour_map)];

            let value_colours = value.split(",");
            let colours_chips = [];
            for (let colour of value_colours) {
                if (materials.colour_arrays?.[colour]) {
                    let list_chips = [];
                    for (let list_colour of materials.colour_arrays?.[colour])
                        list_chips.push(
                            <>
                                ●&nbsp;
                                <ColourChip
                                    className="colour-chip"
                                    colour={list_colour.trim()}
                                    width="1rem"
                                    style={{ background: "#00000000" }}
                                />
                                <span style={{ marginLeft: "5px", fontSize: "12px" }}>{list_colour}</span>
                                <br />
                            </>
                        );

                    colours_chips.push(
                        <>
                            <Chip
                                style={{ background: "#00000000" }}
                                label={
                                    <>
                                        <ColourChip
                                            className="colour-chip"
                                            colour={colour.trim()}
                                            width="1rem"
                                            style={{ background: "#00000000" }}
                                        />
                                        <span style={{ marginLeft: "5px", fontSize: "13px" }}>{colour} (List)</span>
                                    </>
                                }
                                onDelete={() => {
                                    let new_value = "";
                                    for (let val_colour of value_colours) {
                                        if (val_colour.trim() !== colour.trim())
                                            new_value += (new_value == "" ? "" : ",") + val_colour;
                                    }
                                    setValue(new_value.trim());
                                }}
                            />
                            <br />
                            <div style={{ marginLeft: "30px", fontSize: "12px", lineHeight: "1.5" }}>{list_chips}</div>
                        </>
                    );
                } else {
                    colours_chips.push(
                        <>
                            <Chip
                                style={{ background: "#00000000", height: "20px" }}
                                label={
                                    <>
                                        <ColourChip
                                            className="colour-chip"
                                            colour={colour.trim()}
                                            width="1rem"
                                            style={{ background: "#00000000" }}
                                        />
                                        <span style={{ marginLeft: "5px", fontSize: "13px" }}>{colour}</span>
                                    </>
                                }
                                onDelete={() => {
                                    let new_value = "";
                                    for (let val_colour of value_colours) {
                                        if (val_colour.trim() !== colour.trim())
                                            new_value += (new_value == "" ? "" : ",") + val_colour;
                                    }
                                    setValue(new_value.trim());
                                }}
                            />
                            <br />
                        </>
                    );
                }
            }

            let helperText = [];
            if (original_value && param !== "NO BUTTON")
                helperText.push(
                    <>
                        <b>currently: </b>
                        {original_value}
                        <br />
                    </>
                );

            return (
                <>
                    <BSL_TextInput
                        label={"Colours (Raw Text)"}
                        placeholder={
                            value
                                ? ""
                                : "(default:\u00A0" +
                                  defaultColour(row?.ProductDescription, row?.GroupingName, row?.Brand) +
                                  ")"
                        }
                        style={{ width: "300px", color: "white" }}
                        value={value}
                        multiline
                        tight={true}
                        onChange={(value) => {
                            setValue(value);
                        }}
                    />
                    <div style={{ width: "100%", textAlign: "left" }}>{colours_chips}</div>
                    <Autocomplete
                        items={colour_keys}
                        shouldItemRender={(item, value) => {
                            if (item === null || item === undefined || value === null) return true;
                            if (!item.toLowerCase || !value.toLowerCase) return true;
                            return item.toLowerCase().indexOf(value.toLowerCase()) > -1;
                        }}
                        autoHighlight={false}
                        getItemValue={(item) => item}
                        renderItem={(item, highlighted) => (
                            <div key={item.id} style={{ backgroundColor: highlighted ? "#eee" : "transparent" }}>
                                {item}
                            </div>
                        )}
                        wrapperStyle={{
                            zIndex: "999999",
                        }}
                        menuStyle={{
                            zIndex: "999999",
                            position: "absolute",
                            backgroundColor: "whitesmoke",
                            boxShadow: "1px 1px 4px",
                            fontSize: "12px",
                            left: "auto",
                            top: "auto",
                            marginLeft: "9px",
                            marginTop: "-21px",
                            textAlign: "left",
                            maxHeight: "250px",
                            overflowY: "auto",
                        }}
                        inputProps={{
                            style: {
                                zIndex: "999999",
                                font: "200 12px Univers45",
                                width: "95%",
                                padding: "4px 0px 4px 8px !important",
                            },
                        }}
                        renderInput={(props) => {
                            return (
                                <BSL_TextInput
                                    label={"Add New Colour"}
                                    style={{ width: "300px", color: "white" }}
                                    value={value2}
                                    tight={true}
                                    onEnter={(e) => {
                                        let new_col = e.target.value.replace("List: ", "").split("(")[0].trim();
                                        setValue(value + (value == "" ? "" : ",") + new_col);
                                        setValue2("");
                                    }}
                                    onChange={(value) => {
                                        setValue2(value);
                                    }}
                                    InputProps={{
                                        style: { fontSize: "80%" },
                                        ...props,
                                    }}
                                    helperText={helperText}
                                    helperTextStyle={
                                        helperText.length > 0
                                            ? {
                                                  whiteSpace: "break-spaces",
                                                  backgroundColor: "#ffe7c3",
                                                  padding: "5px",
                                                  borderRadius: "5px",
                                              }
                                            : {}
                                    }
                                />
                            );
                        }}
                        value={value2}
                        onChange={(e) => setValue2(e.target.value)}
                        onSelect={(val) => {
                            let new_col = val.replace("List: ", "").split("(")[0].trim();
                            setValue((value + (value == "" ? "" : ",") + new_col).trim());
                            setValue2("");
                        }}
                    />
                    {param !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "Colours", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "AncillaryParts",
        label: "Ancillary Parts",
        format: (value, row) => {
            if (typeof value === "object" || value.startsWith("{")) {
                var parts = "";
                var ancillary_json = typeof value === "object" ? value : JSON.parse(value);
                if (ancillary_json["AncillaryParts"])
                    for (var item of ancillary_json["AncillaryParts"]) {
                        var qty = item?.Qty;
                        if (qty === "" || qty === undefined) qty = "1";
                        else if (qty && qty?.includes && qty.includes("=")) qty = "X";
                        var code = item?.Code ?? "";
                        var conditions = item?.Conditions ? "*" : "";
                        if (item?.Conditions && JSON.stringify(item?.Conditions).includes("Lysaght"))
                            conditions += "Lysaght*";
                        if (item?.Conditions && JSON.stringify(item?.Conditions).includes("Fielders"))
                            conditions += "Fielders*";
                        var use = item?.Use ? "(" + item?.Use + ")" : "";
                        if (code != "") parts += qty + "x" + code + use + conditions + "\n";
                    }
                return <div style={{ color: "darkred", fontSize: "66%", whiteSpace: "break-spaces" }}>{parts}</div>;
            } else {
                let parts = "";
                let split = value.split(" ");
                for (let i = 0; i < split.length; i++) {
                    var this_item = split[i];
                    if (this_item !== "") {
                        if (this_item.includes("(") && !this_item.includes(")") && i < split.length) {
                            while (i <= split.length) {
                                i++;
                                let next_bit = split[i];
                                this_item += " " + next_bit;
                                if (next_bit.includes(")")) break;
                            }
                        }
                        parts += this_item + "\n";
                    }
                }

                return <div style={{ color: "darkgrey", fontSize: "8px", whiteSpace: "break-spaces" }}>{parts}</div>;
            }
        },
        editor: (row, value, setValue, saveValue, value2, setValue2, pricefileDescSummary) => {
            if (!row?.ProductCode) return null;
            return (
                <ManageProductListAncillary
                    row={row}
                    onClose={() => saveValue(null)}
                    saveValue={saveValue}
                    pricefileDescSummary={pricefileDescSummary}
                />
            );
        },
    },
    {
        id: "PromptTextCodes",
        label: "Control Codes",
        format: (value, row) => {
            let arr = null;
            if (value?.replace)
                arr = value
                    .replace(/("[\s\S]*?")/g, (m, cg) => {
                        return cg.replace(/\s/g, "LINE-BREAK-TO-PRESERVE");
                    })
                    .split(" ")
                    .filter((i) => Boolean(i.trim()))
                    .map((i) => i.replace(/LINE-BREAK-TO-PRESERVE/g, " "));

            let val_list = "";
            if (arr)
                for (let line of arr) {
                    let output = line;
                    if (line.startsWith("SL")) {
                        output = "";
                        let sls = line.split("|");
                        for (let sl of sls) {
                            output += (output === "" ? "" : "|") + sl.split("(")[0];
                        }
                    }
                    val_list += (output.length > 30 ? output.substring(0, 30) + "..." : output) + "\n";
                }

            return <div style={{ color: "gray", fontSize: "66%", whiteSpace: "break-spaces" }}>{val_list}</div>;
        },
        editor: (row, value, setValue, saveValue, value2, setValue2, pricefileDescSummary) => {
            if (!row?.ProductCode) return null;
            return (
                <ManageProductListControlCodes
                    row={row}
                    onClose={() => saveValue(null)}
                    saveValue={saveValue}
                    pricefileDescSummary={pricefileDescSummary}
                />
            );
        },
    },
    {
        id: "Notes",
        label: "Notes",
        format: (value, row) => {
            let output = value;
            if (output === undefined) output = "";
            return <div style={{ color: "gray", fontSize: "66%", whiteSpace: "normal" }}>{output}</div>;
        },
        editor: (row, value, setValue, saveValue, value2) => {
            if (value === undefined) setValue(row.Notes ? row.Notes : "");
            let original_value = row.Notes ? row.Notes : "";
            return (
                <>
                    <BSL_TextInput
                        label={"Notes"}
                        style={{ width: "300px", color: "white" }}
                        multiline
                        autoFocus
                        value={value}
                        tight={true}
                        onEnter={() => saveValue(row, "Notes", value)}
                        onChange={(value) => {
                            setValue(value);
                        }}
                        helperText={(value ? value.length : 0) + "/150 characters."}
                    />

                    {value2 !== "NO BUTTON" && (
                        <BSL_Button
                            onClick={() => {
                                if (value.length > 150) {
                                    alert("max 150 charaters exceeded");
                                    return;
                                }
                                saveValue(row, "Notes", value);
                            }}
                        >
                            UPDATE
                        </BSL_Button>
                    )}
                </>
            );
        },
    },
    {
        id: "Hidden",
        label: "Hide in BOM",
        format: (value, row) => {
            let output = value;
            if (output === "1" || output === 1) return "Yes";
            return <span style={{ color: "#80808070" }}>No</span>;
        },
        editor: (row, value, setValue, saveValue, value2) => {
            return (
                <>
                    <SegmentedButtonGroup
                        label={"Hide in BOM"}
                        style={{ width: "200px", flex: "none" }}
                        value={value}
                        tight={true}
                        items={[
                            { Key: "0", Display: "No" },
                            { Key: "1", Display: "Yes" },
                        ]}
                        onChange={(e, val) => {
                            setValue(val);
                            e.stopPropagation();
                        }}
                    />

                    <BSL_Button
                        onClick={() => {
                            saveValue(row, "Hidden", value);
                        }}
                    >
                        UPDATE
                    </BSL_Button>
                </>
            );
        },
    },
    {
        id: "State",
        label: "States",
        hideInTable: true,
        editor: (row, value, setValue, saveValue, value2) => {
            // if (value === undefined) setValue(row.Cost);
            // let original_value = row.Cost;
            const allStates = ["NSW", "QLD", "VIC", "TAS", "SA", "WA", "NT"];

            const customStyles = {
                container: (provided) => ({
                    ...provided,
                    margin: "2px 2px 0px 8px",
                }),
                menu: (provided) => ({
                    ...provided,
                    border: "1px solid " + Palette.Control.DarkBlueTrans,
                }),
                option: (provided) => ({
                    ...provided,
                    fontSize: "14px !important",
                }),
                input: (provided) => ({
                    ...provided,
                    fontSize: "14px !important",
                }),
                // Add more styles as needed
            };

            return (
                <>
                    <div style={{ fontSize: "14px" }}>
                        <label htmlFor="lldDropdown">available in LLD: </label>
                        <Select
                            isMulti
                            id="lldDropdown"
                            options={allStates.map((state) => ({ value: state, label: state }))}
                            value={(value.LLD || []).map((selectedState) => ({
                                value: selectedState,
                                label: selectedState,
                            }))}
                            onChange={(selectedOptions) => {
                                const selectedLLD = selectedOptions.map((option) => option.value);
                                setValue({
                                    ...value,
                                    LLD: selectedLLD,
                                    CDS: value.CDS || [],
                                });
                            }}
                            styles={customStyles}
                        />
                    </div>

                    <div style={{ fontSize: "14px" }}>
                        <label htmlFor="cdsDropdown">available in CDS: </label>
                        <Select
                            isMulti
                            id="cdsDropdown"
                            options={allStates.map((state) => ({ value: state, label: state }))}
                            value={(value.CDS || []).map((selectedState) => ({
                                value: selectedState,
                                label: selectedState,
                            }))}
                            onChange={(selectedOptions) => {
                                const selectedCDS = selectedOptions.map((option) => option.value);
                                setValue({
                                    ...value,
                                    CDS: selectedCDS,
                                    LLD: value.LLD || [],
                                });
                            }}
                            styles={customStyles}
                        />
                    </div>

                    {value2 !== "NO BUTTON" && (
                        <BSL_Button onClick={() => saveValue(row, "State", value)}>UPDATE</BSL_Button>
                    )}
                </>
            );
        },
    },

    // {
    //     id: "MatchedItems",
    //     label: "Dynamics Codes",
    //     hideInTable: false,
    //     editor: (row, value, setValue, saveValue, value2) => {
    //         return <D365Editor row={row} value={value} setValue={setValue} saveValue={saveValue} value2={value2} />;
    //     },
    // },
];

const menu_columm = {
    id: "Menu",
    label: "Menu",
    editor: (row, value, setValue, saveValue, value2, setValue2, not_used, not_used2, setEditItem) => {
        if (!value2) setValue2({ ...row });

        let new_row = value2 ? { ...value2 } : { ...row };

        return (
            <>
                <BSL_Button
                    style={{
                        margin: "28px 0px 5px",
                        padding: "5px 5px",
                        width: "100%",
                    }}
                    onClick={() => {
                        setValue("");
                        setValue2("");
                        setEditItem({
                            action: "EDIT",
                            row: row,
                        });
                    }}
                >
                    EDIT
                </BSL_Button>
                <br />
                <BSL_Button
                    style={{
                        margin: "0px 0px 5px",
                        width: "100%",
                    }}
                    onClick={() => {
                        setValue("");
                        setValue2("");
                        setEditItem({
                            action: "DUPLICATE",
                            row: row,
                        });
                    }}
                >
                    DUPLICATE
                </BSL_Button>
                <br />
                <BSL_Button
                    style={{
                        margin: "0px 0px 5px",
                        width: "100%",
                    }}
                    onClick={() => {
                        setValue("HISTORY");
                        setValue2(row);
                    }}
                >
                    HISTORY
                </BSL_Button>
                <br />
                <BSL_Button
                    style={{
                        margin: "0px 0px 5px",
                        width: "100%",
                        backgroundColor: "darkred",
                    }}
                    className={value === "DELETE" ? "deleteButtonConfirm" : "deleteButton"}
                    onClick={() => {
                        if (value === "DELETE") saveValue(row, "DELETE");
                        else setValue("DELETE");
                    }}
                >
                    <DeleteIcon />
                    {value === "DELETE" ? "CONFIRM?" : "DELETE"}
                </BSL_Button>
            </>
        );
    },
};

export default function ManageProductList(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const app_pricefile = useSelector((state) => state.app.pricefile);

    // const { materials, setMaterials, pricefile, setPricefile, pricefileDescSummary, setPricefileDescSummary } = props;

    // const config = useSelector((state) => state.config);
    const [intialise, setIntialise] = React.useState(false);
    // const [pricefile, setPricefile] = React.useState(null);
    // const [pricefileDescSummary, setPricefileDescSummary] = React.useState(null);
    const [cdsOffering, setCDSOffering] = React.useState(null);
    const [lldOffering, setLLDOffering] = React.useState(null);

    const [materials, setMaterials] = React.useState(null);
    const [pricefile, setPricefile] = React.useState(null);
    const [pricefileDescSummary, setPricefileDescSummary] = React.useState(null);
    // const [cds_docs, setCDSOffering] = React.useState(null);
    // const [lld_docs, setLLDOffering] = React.useState(null);
    const [materialsKeys, setMaterialsKeys] = React.useState(null);

    // const [materials, setMaterials] = React.useState(null);
    // const [mergedOffering, setMergedOffering] = React.useState(null);

    const [tableRows, setTableRows] = React.useState([]);

    const [searchFilter, setSearchFilter] = React.useState("");
    const [categoryFilter, setCategoryFilter] = React.useState("");
    const [brandFilter, setBrandFilter] = React.useState("");
    const [editCell, setEditCell] = React.useState(null);
    const [showLoading, setShowLoading] = React.useState(false);
    const [saving, setSaving] = React.useState({}); // not fast enough! hackign with a global var
    if (window.saving === undefined) window.saving = {};

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(100);
    const [numFilteredItems, setNumFilteredItems] = React.useState(0);

    const [value, setValue] = React.useState("");
    const [value2, setValue2] = React.useState("");

    const [editItem, setEditItem] = React.useState(false);
    const [tabHasFocus, setTabHasFocus] = React.useState(true);

    useEffect(() => {
        const handleFocus = () => {
            setTabHasFocus(true);
            setIntialise(false);
        };

        const handleBlur = () => {
            setTabHasFocus(false);
        };

        window.addEventListener("focus", handleFocus);
        window.addEventListener("blur", handleBlur);

        return () => {
            window.removeEventListener("focus", handleFocus);
            window.removeEventListener("blur", handleBlur);
        };
    }, []);

    React.useEffect(() => {
        if (!intialise) {
            setShowLoading(true);
            let api_url = API_BASE() + "admin/GetPriceFileData";
            fetchTimeout(
                api_url,
                {
                    method: "GET",
                },
                90000
            )
                .then((response) => response.json())
                .then((json) => {
                    let pricefile = json["PRICE_FILE"];
                    pricefile["data"].sort((a, b) => {
                        return a.ProductIndex.localeCompare(b.ProductIndex);
                    });

                    //RehydratePriceFile
                    pricefile = RehydrateThumbnails(pricefile);

                    setPricefile(pricefile);

                    setCDSOffering(json["CEN_OFFERING"]);
                    setLLDOffering(json["LLD_OFFERING"]);
                    if (json?.["MATERIALS"]) setMaterials(json["MATERIALS"]);

                    setIntialise(true);
                    setShowLoading(false);
                })
                .catch((e) => {
                    setIntialise(true);

                    setPricefile(null);
                    setShowLoading(false);
                });
        }
    }, [intialise]);

    React.useEffect(() => {
        let table_rows = [];
        let row_i = 0;
        let start_i = page * rowsPerPage;
        let end_i = start_i + rowsPerPage;
        if (rows)
            rows.map((row, index) => {
                if (
                    searchFilter &&
                    !(
                        row.ProductCode +
                        row.ProductIndex +
                        row.ProductDescription +
                        row.PromptTextCodes +
                        row.AncillaryParts
                    )
                        .toUpperCase()
                        .includes(searchFilter.toUpperCase())
                )
                    return null;
                if (brandFilter && brandFilter != "" && row.Brand !== brandFilter) return null;
                if (categoryFilter && categoryFilter != "" && !row.GroupingName.includes(categoryFilter)) return null;

                row_i++;

                if (row_i < start_i) return null;
                if (row_i > end_i) return null;

                table_rows.push(
                    <TableRow
                        key={/*index +*/ row.id}
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        className={classes.row}
                        style={{ background: row_i % 2 === 0 ? "#e7ebee" : "#ffffff" }}
                    >
                        {columns.map((column) => {
                            if (column?.hideInTable) return null;
                            let value = row[column.id];

                            return (
                                <TableCell
                                    key={row.id + "-" + column.id}
                                    align={column.align}
                                    className={classes.rowCell}
                                    style={column.style ? column.style : {}}
                                    onClick={(e) => {
                                        if (editCell || column?.editor === undefined) {
                                            setValue("");
                                            setValue2("");
                                            setEditCell(null);
                                        } else {
                                            setValue(value);
                                            setValue2("");
                                            setEditCell({
                                                anchorEl: e.currentTarget,
                                                row: row,
                                                column: { ...column },
                                            });
                                        }
                                        e.stopPropagation();
                                    }}
                                >
                                    {column.format && value !== undefined ? column.format(value, row) : value}
                                    {window.saving &&
                                        window.saving?.[row.id] &&
                                        column?.id === window.saving?.[row.id] && <LinearProgress />}
                                    {window.saving &&
                                        window.saving?.[row.id] &&
                                        "Error:" + column?.id === window.saving?.[row.id] && (
                                            <ErrorIcon style={{ color: "red" }} />
                                        )}
                                </TableCell>
                            );
                        })}
                        <TableCell
                            key={row.code + "-Menu"}
                            className={classes.rowCell}
                            onClick={(e) => {
                                if (editCell || menu_columm?.editor === undefined) {
                                    setValue("");
                                    setValue2("");
                                    setEditCell(null);
                                } else {
                                    setValue(value);
                                    setEditCell({
                                        anchorEl: e.currentTarget,
                                        row: row,
                                        column: { ...menu_columm },
                                    });
                                }
                                e.stopPropagation();
                            }}
                        >
                            <IconButton className={classes.moreButton}>
                                <MoreVertIcon />
                            </IconButton>
                            {window.saving &&
                                window.saving?.[row.id] &&
                                menu_columm?.id === window.saving?.[row.id] && <LinearProgress />}
                            {window.saving &&
                                window.saving?.[row.id] &&
                                "Error:" + menu_columm?.id === window.saving?.[row.id] && (
                                    <ErrorIcon style={{ color: "red" }} />
                                )}
                        </TableCell>
                    </TableRow>
                );
            });
        setNumFilteredItems(row_i);
        setTableRows(table_rows);
    }, [pricefile, searchFilter, brandFilter, categoryFilter, saving, page, rowsPerPage]);

    React.useEffect(() => {
        if (pricefile) {
            let summary = {};
            for (let item of pricefile["data"]) {
                summary[item.ProductIndex] = item.ProductDescription;
            }
            setPricefileDescSummary(summary);
        }
    }, [pricefile]);

    let noJobMessage = "Items Loading...";
    let rows = pricefile?.["data"];

    const CustomTooltip = withStyles({
        tooltip: {
            color: "write",
            backgroundColor: "white",
            fontWeight: "bold",
            fontSize: "12px",
            border: "1px solid #8A8B8A",
            maxWidth: "400px",
            marginLeft: "200px",
            "& > span": {
                left: "5px !important",
                color: "#8A8B8A",
            },
        },
    })(Tooltip);

    const saveValue = (row, parameter, value, parameter2 = null, value2) => {
        setEditCell(null);
        setValue("");
        setValue2("");

        if (!row) {
            return;
        }

        if (parameter2 && row[parameter] === value && row[parameter2] === value2) {
            window.saving[row.id] = null;
            setSaving({ ...window.saving });
            return;
        }
        if (
            parameter !== "DELETE" &&
            parameter !== "ADD" &&
            parameter !== "EDIT" &&
            row[parameter] === value &&
            parameter2 === null
        ) {
            window.saving[row.id] = null;
            setSaving({ ...window.saving });
            return;
        }

        let new_row = { ...row };

        //update local
        if (parameter !== "DELETE") {
            // update local database
            if (app_pricefile) {
                let new_app_pricefile = { ...app_pricefile };

                if (parameter == "ADD") {
                    new_app_pricefile["data"].push(new_row);
                } else if (parameter == "EDIT") {
                    for (let new_app_pricefile_row of new_app_pricefile.data) {
                        if (new_app_pricefile_row.ProductIndex == row.ProductIndex) {
                            new_app_pricefile_row = { ...new_row };
                        }
                    }
                } else {
                    for (let new_app_pricefile_row of new_app_pricefile.data) {
                        if (
                            new_app_pricefile_row.ProductIndex == row.ProductIndex &&
                            new_app_pricefile_row[parameter] != null
                        ) {
                            new_app_pricefile_row[parameter] = value;
                        }
                    }
                }
                dispatch(updateAPP("pricefile", new_app_pricefile));
            }
        }

        window.saving[row.id] = parameter;
        setSaving({ ...window.saving });

        let api_url = API_BASE() + "admin/UpdatePriceFile?action=Update_" + parameter.replace(" ", "_");
        if (parameter === "DELETE") {
            api_url = API_BASE() + "admin/UpdatePriceFile?action=delete";
            setShowLoading(true);
        } else if (parameter === "ADD") {
            let max = 0;
            for (let item of pricefile["data"]) {
                if (Number(item.id) > max) {
                    max = Number(item.id);
                }
            }
            new_row.id = max + 1;
            api_url = API_BASE() + "admin/UpdatePriceFile?action=add";
            setShowLoading(true);
        } else if (parameter === "EDIT") {
            setShowLoading(true);
        } else {
            new_row[parameter] = value;
            if (parameter2) new_row[parameter2] = value2;
        }

        fetchTimeout(api_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(new_row),
        })
            .then(async (response) => {
                if (response.status >= 200 && response.status <= 300) {
                    let new_row_server = await response.json();
                    let new_pricefile = { ...pricefile };

                    if (parameter === "DELETE") {
                        new_pricefile["data"] = new_pricefile["data"].filter(function (item) {
                            return item.id !== new_row_server.id;
                        });
                    } else if (parameter === "ADD") {
                        new_pricefile["data"].push(new_row);
                    } else {
                        new_pricefile["data"] = new_pricefile["data"].map((item, index) => {
                            if (item?.id === new_row_server?.id) {
                                return new_row_server;
                            } else {
                                return item;
                            }
                        });
                    }

                    new_pricefile["data"].sort((a, b) => {
                        return a.ProductIndex.localeCompare(b.ProductIndex);
                    });

                    setPricefile(new_pricefile);

                    // update local database
                    if (app_pricefile) {
                        let new_app_pricefile = { ...app_pricefile };

                        for (let new_app_pricefile_row of new_app_pricefile.data) {
                            for (let new_pricefile_row of new_pricefile.data) {
                                if (new_app_pricefile_row.ProductIndex == new_pricefile_row.ProductIndex) {
                                    new_app_pricefile_row.PromptTextCodes = new_pricefile_row.PromptTextCodes;
                                    new_app_pricefile_row.AncillaryParts = new_pricefile_row.AncillaryParts;
                                }
                            }
                        }

                        dispatch(updateAPP("pricefile", new_app_pricefile));
                    }

                    window.saving[row.id] = null;
                    setSaving({ ...window.saving });
                } else {
                    throw "Error saving values";
                }
                window.saving[row.id] = null;
                setSaving({ ...window.saving });
                setShowLoading(false);
            })
            .catch((e) => {
                //try again
                fetchTimeout(api_url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(new_row),
                })
                    .then(async (response) => {
                        if (response.status >= 200 && response.status <= 300) {
                            let new_row_server = await response.json();
                            let new_pricefile = { ...pricefile };

                            if (parameter === "DELETE") {
                                new_pricefile["data"] = new_pricefile["data"].filter(function (item) {
                                    return item.id !== new_row_server.id;
                                });
                            } else if (parameter === "ADD") {
                                new_pricefile["data"].push(new_row);
                            } else {
                                new_pricefile["data"] = new_pricefile["data"].map((item, index) => {
                                    if (item?.id === new_row_server?.id) {
                                        return new_row_server;
                                    } else {
                                        return item;
                                    }
                                });
                            }

                            new_pricefile["data"].sort((a, b) => {
                                return a.ProductIndex.localeCompare(b.ProductIndex);
                            });
                            setPricefile(new_pricefile);

                            window.saving[row.id] = null;
                            setSaving({ ...window.saving });
                        } else {
                            throw "Error saving values";
                        }
                        window.saving[row.id] = null;
                        setSaving({ ...window.saving });
                        setShowLoading(false);
                    })
                    .catch((e) => {
                        // setSaving({ ...saving, [row.id]: "Error:" + saving[row.id] });
                        window.saving[row.id] = "Error:" + window.saving[row.id];
                        setSaving({ ...window.saving });
                        setShowLoading(false);
                    });
            });
    };

    const deleteRow = (row) => {
        setEditCell(null);
        setValue("");
        setValue2("");

        if (!row) {
            return;
        }
        window.saving[row.id] = menu_columm.id;
        setSaving({ ...window.saving });

        let api_url = API_BASE() + "admin/UpdatePriceFile?action=delete";
        fetchTimeout(api_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(row),
        })
            .then(async (response) => {
                if (response.status >= 200 && response.status <= 300) {
                    let new_row_server = await response.json();
                    let new_pricefile = { ...pricefile };

                    new_pricefile["data"] = new_pricefile["data"].filter(function (item) {
                        return item.id !== new_row_server.id;
                    });
                    new_pricefile["data"].sort((a, b) => {
                        return a.ProductIndex.localeCompare(b.ProductIndex);
                    });
                    setPricefile(new_pricefile);

                    window.saving[row.id] = null;
                    setSaving({ ...window.saving });
                } else {
                    throw "Error saving values";
                }
                window.saving[row.id] = null;
                setSaving({ ...window.saving });
            })
            .catch((e) => {
                // setSaving({ ...saving, [row.id]: "Error:" + saving[row.id] });
                window.saving[row.id] = "Error:" + window.saving[row.id];
                setSaving({ ...window.saving });
            });
    };

    let new_row = value2 ? { ...value2 } : {};

    return (
        <Paper className={classes.root} onClick={(e) => setEditCell(null)}>
            <div
                style={{
                    display: "flex",
                    width: "100%",
                    padding: "5px",
                    alignItems: "self-end",
                }}
            >
                <BSL_Button
                    style={{
                        margin: "0px",
                    }}
                    onClick={(e) => {
                        setEditItem({
                            action: "NEW",
                            row: {
                                id: "",
                                ProductCode: "",
                                ProductDescription: "",
                                ProductIndex: "",
                                MatchedItems: [],
                                GroupingName: categoryFilter ? categoryFilter : "Misc",
                                ProductGroup: "",
                                SubGroup: "",
                                SellingUOM: "EA",
                                Cost: "",
                                MfgLocations: "",
                                Markup: "1",
                                MarkupCarport: "0",
                                SellingUnitWeight: "",
                                PromptTextCodes: "",
                                Colours: "",
                                LiveCost: "",
                                AncillaryParts: "",
                                Image_b64: "",
                                Brand: "",
                                Hidden: "0",
                                State: {},
                            },
                        });
                    }}
                    disabled={!pricefile}
                >
                    ADD NEW
                </BSL_Button>

                <BSL_Select
                    label={"Category"}
                    style={{ width: "300px", flex: "none" }}
                    value={categoryFilter}
                    tight={true}
                    items={[
                        { Key: "", Display: "ALL" },
                        { Key: "Steel Roofing", Display: "Steel Roofing" },
                        { Key: "PolyCarbonate Roofing", Display: "PolyCarbonate Roofing" },
                        { Key: "Fibreglass Roofing", Display: "Fibreglass Roofing" },
                        { Key: "Back Channel", Display: "Back Channel" },
                        { Key: "Downpipes", Display: "Downpipes & Accessories" },
                        { Key: "Gutters", Display: "Gutters & Accessories" },
                        { Key: "Flashing", Display: "Flashing" },
                        { Key: "Beams", Display: "Beams" },
                        { Key: "Posts", Display: "Posts" },
                        { Key: "Brackets", Display: "Brackets" },
                        { Key: "Connectors", Display: "Connectors" },
                        { Key: "Hardware", Display: "Hardware" },
                        { Key: "Slats", Display: "Slats" },
                        { Key: "Misc", Display: "Misc" },
                    ]}
                    onChange={(e) => {
                        setCategoryFilter(e.target.value);
                        setPage(0);
                    }}
                />
                <BSL_Select
                    label={"Brand"}
                    style={{ width: "90px", flex: "none" }}
                    value={brandFilter}
                    tight={true}
                    items={[
                        { Key: "", Display: "ALL" },
                        { Key: "CDS", Display: "CDS" },
                        { Key: "LLD", Display: "LLD" },
                    ]}
                    onChange={(e) => {
                        setBrandFilter(e.target.value);
                        setPage(0);
                    }}
                />
                <div style={{ flex: 2 }} />
                <div className={classes.search_container}>
                    <SearchIcon className={classes.search_icon} />
                    <TextField
                        id="Search"
                        placeholder="Search"
                        type="text"
                        value={searchFilter}
                        onKeyPress={(e) => {
                            if (e.key === "Enter") {
                                e.target.blur();
                            }
                        }}
                        InputProps={{
                            disableUnderline: true,
                        }}
                        onChange={(e) => {
                            // if (categoryFilter !== "");
                            setSearchFilter(e.target.value);
                        }}
                        onBlur={(e) => {
                            setSearchFilter(e.target.value);
                            setPage(0);
                        }}
                    />
                    <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={() => {
                            setSearchFilter("");
                            setPage(0);
                        }}
                        style={{}}
                    >
                        <CloseIcon fontSize="inherit" />
                    </IconButton>
                </div>
            </div>
            <TableContainer className={classes.container}>
                <Table stickyHeader size="small" aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) =>
                                column?.hideInTable ? null : (
                                    <TableCell
                                        key={column.id}
                                        align={column.align}
                                        style={{ minWidth: column.minWidth }}
                                        className={classes.headingCell}
                                    >
                                        {column.label}
                                    </TableCell>
                                )
                            )}
                            {/* <TableCell key={"Edit"} className={classes.headingCell}></TableCell> */}
                            <TableCell key={"Options"} className={classes.headingCell}></TableCell>
                        </TableRow>
                    </TableHead>
                    {(!rows || rows.length === 0 || showLoading) && (
                        <LinearProgress
                            style={{
                                position: "absolute",
                                width: "96.5%",
                            }}
                        />
                    )}
                    <TableBody>{tableRows}</TableBody>
                    {(!rows || rows.length === 0) && <div className={classes.no_job_message}>{noJobMessage}</div>}
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[50, 100, 250, 500, 1000, 3000]}
                component="div"
                count={numFilteredItems}
                rowsPerPage={rowsPerPage}
                page={page}
                labelDisplayedRows={({ from, to, count }) => {
                    return (
                        from +
                        "-" +
                        to +
                        " of " +
                        (count !== -1 ? count : "more than " + to) +
                        (rows && count < rows.length ? " (" + rows.length + " total)" : "")
                    );
                }}
                onChangePage={(event, newPage) => {
                    setPage(newPage);
                }}
                onChangeRowsPerPage={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                }}
                style={{
                    maxHeight: "39px",
                    overflow: "hidden",
                    marginTop: "-10px",
                }}
            />
            {/* <Dialog open={editCell} onClose={() => setEditCell(null)}>
                <DialogTitle>
                    Editing '{editCell?.col_id}' for '{editCell?.row?.ProductCode}'
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        To subscribe to this website, please enter your email address here. We will send updates
                        occasionally.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <BSL_Button onClick={() => setEditCell(null)}>Cancel</BSL_Button>
                    <BSL_Button onClick={() => setEditCell(null)}>Subscribe</BSL_Button>
                </DialogActions>
            </Dialog> */}
            {editItem && (
                <ManageProductListItem
                    action={editItem.action}
                    row={editItem.row}
                    columns={columns}
                    onClose={() => {
                        setEditItem(false);
                    }}
                    config={{
                        cdsOffering: cdsOffering,
                        lldOffering: lldOffering,
                        // mergedOffering: mergedOffering,
                        pricefileDescSummary: pricefileDescSummary,
                        materials: materials,
                    }}
                    saveValue={(row, param) => {
                        saveValue(row, param);
                        setEditItem(false);
                    }}
                />
            )}
            {value === "HISTORY" && (
                <ManageProductListItemHistory
                    row={value2}
                    onClose={() => {
                        setEditItem(false);
                        setValue("");
                        setValue2("");
                    }}
                    columns={columns}
                    config={{
                        cdsOffering: cdsOffering,
                        lldOffering: lldOffering,
                        // mergedOffering: mergedOffering,
                        pricefileDescSummary: pricefileDescSummary,
                        materials: materials,
                    }}
                    // saveValue={(row, param) => {
                    //     saveValue(row, param);
                    //     setEditItem(false);
                    // }}
                />
            )}
            <Popper
                key={editCell}
                anchorEl={editCell?.anchorEl}
                placement="bottom-start"
                open={editCell?.column?.editor !== undefined || value === "NEW"}
                disablePortal={false}
                modifiers={{
                    flip: {
                        enabled: true,
                    },
                    preventOverflow: {
                        enabled: true,
                        boundariesElement: "scrollParent",
                    },
                    arrow: {
                        enabled: true,
                        element: editCell?.anchorEl,
                    },
                }}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                {" "}
                <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
                    <Paper
                        style={{
                            padding: "0px 5px 5px 5px",
                            background: "rgb(255 179 67 / 96%)",
                            textAlign: "center",
                            paddingTop: "2px",
                        }}
                    >
                        <div
                            id="draggable-dialog-title"
                            style={{
                                position: "absolute",
                                width: "100%",
                                height: "19px",
                            }}
                        >
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <div className={classes.drag_bar} />
                                <div className={classes.drag_bar} />
                                <div className={classes.drag_bar} />
                            </div>
                        </div>
                        <IconButton
                            aria-label="delete"
                            size="small"
                            onClick={() => setEditCell(null)}
                            style={{ color: "white", position: "absolute", right: "5px" }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {editCell?.column?.editor(
                            editCell?.row,
                            value,
                            setValue,
                            saveValue,
                            value2,
                            setValue2,
                            pricefileDescSummary,
                            materials,
                            // editCell?.row["Brand"] === "CDS"
                            //     ? cdsOffering
                            //     : editCell?.row["Brand"] === "LLD"
                            //     ? lldOffering
                            //     : {},
                            // : mergedOffering,
                            setEditItem
                        )}
                    </Paper>
                </Draggable>
            </Popper>
        </Paper>
    );
}
